// Only cash-photo blobs are stored offline. Financial entries are never queued.
type Queued = { key: string; workspaceId: string; userId: string; movementId: string; targetKind?: "count" | "expense-record"; blob: Blob; createdAt: number };
const STORE = "uploads";
const MAX_AGE = 7 * 24 * 60 * 60 * 1000;
function database(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open("branch-cash-photo-uploads", 1);
    request.onupgradeneeded = () => request.result.createObjectStore(STORE, { keyPath: "key" });
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}
async function transact<T>(mode: IDBTransactionMode, callback: (store: IDBObjectStore, resolve: (value: T) => void, reject: (error: unknown) => void) => void): Promise<T> {
  const db = await database();
  try {
    return await new Promise<T>((resolve, reject) => {
      const transaction = db.transaction(STORE, mode);
      transaction.oncomplete = () => db.close();
      transaction.onerror = () => reject(transaction.error);
      callback(transaction.objectStore(STORE), resolve, reject);
    });
  } catch (error) { db.close(); throw error; }
}
export async function queueCashPhoto(workspaceId: string, userId: string, movementId: string, blob: Blob, targetKind?: "count" | "expense-record") {
  const key = crypto.randomUUID();
  await transact<void>("readwrite", (store, resolve, reject) => {
    const request = store.put({ key, workspaceId, userId, movementId, targetKind, blob, createdAt: Date.now() } satisfies Queued);
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
  window.dispatchEvent(new Event("cash-photo-queued"));
  return key;
}
export async function pendingCashPhotos(workspaceId: string, userId: string): Promise<Queued[]> {
  const rows = await transact<Queued[]>("readonly", (store, resolve, reject) => {
    const request = store.getAll();
    request.onsuccess = () => resolve(request.result as Queued[]);
    request.onerror = () => reject(request.error);
  });
  const expired = rows.filter(row => !row.createdAt || row.createdAt + MAX_AGE < Date.now());
  if (expired.length) await Promise.all(expired.map(row => discardCashPhoto(row.key)));
  return rows.filter(row => row.workspaceId === workspaceId && row.userId === userId && !expired.includes(row));
}
async function discardCashPhoto(key: string): Promise<void> {
  await transact<void>("readwrite", (store, resolve, reject) => {
    const request = store.delete(key);
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
}
export async function clearCashPhotosOnLogout(userId: string): Promise<number> {
  const rows = await transact<Queued[]>("readonly", (store, resolve, reject) => {
    const request = store.getAll();
    request.onsuccess = () => resolve(request.result as Queued[]);
    request.onerror = () => reject(request.error);
  });
  const own = rows.filter(row => row.userId === userId);
  for (const row of own) await discardCashPhoto(row.key);
  return own.length;
}
export async function countCashPhotosOnDevice(userId: string): Promise<number> {
  const rows = await transact<Queued[]>("readonly", (store, resolve, reject) => {
    const request = store.getAll();
    request.onsuccess = () => resolve(request.result as Queued[]);
    request.onerror = () => reject(request.error);
  });
  return rows.filter(row => row.userId === userId).length;
}
export async function sendCashPhoto(row: Queued): Promise<void> {
  const res = await fetch(`/api/cash/${row.targetKind === "count" ? "counts" : row.targetKind === "expense-record" ? "expense-records" : "movements"}/${encodeURIComponent(row.movementId)}/photos`, {
    method: "POST", credentials: "include",
    headers: { "Content-Type": row.blob.type, "x-workspace-id": row.workspaceId }, body: row.blob,
  });
  if (!res.ok) {
    const json = await res.json().catch(() => null);
    throw Error(json?.message || `Photo upload failed (${res.status}). The photo is saved on this phone; retry when online.`);
  }
  await transact<void>("readwrite", (store, resolve, reject) => {
    const request = store.delete(row.key);
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
}
const uploads = new Map<string, Promise<void>>();
export function uploadPendingCashPhotos(workspaceId: string, userId: string): Promise<void> {
  const owner = `${workspaceId}:${userId}`;
  const existing = uploads.get(owner);
  if (existing) return existing;
  const run = uploadRows(workspaceId, userId).finally(() => uploads.delete(owner));
  uploads.set(owner, run);
  return run;
}
async function uploadRows(workspaceId: string, userId: string) {
  if (!navigator.onLine) throw Error("No connection. The photo is saved on this phone and will send when back online.");
  const rows = await pendingCashPhotos(workspaceId, userId);
  for (const row of rows) await sendCashPhoto(row);
}
export async function shrinkCashPhoto(file: File): Promise<Blob> {
  if (!/^image\/(jpeg|png|webp)$/.test(file.type)) throw Error("Choose a JPEG, PNG or WebP photo.");
  const bitmap = await createImageBitmap(file);
  try {
    const factor = Math.min(1, 1600 / Math.max(bitmap.width, bitmap.height));
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.round(bitmap.width * factor));
    canvas.height = Math.max(1, Math.round(bitmap.height * factor));
    const ctx = canvas.getContext("2d");
    if (!ctx) throw Error("This phone cannot process the photo.");
    ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    for (const quality of [0.82, 0.66, 0.48]) {
      const blob = await new Promise<Blob | null>(resolve => canvas.toBlob(resolve, "image/jpeg", quality));
      if (blob && blob.size <= 3 * 1024 * 1024) return blob;
    }
    throw Error("Photo remains over 3 MB after shrinking. Take a smaller photo.");
  } finally { bitmap.close(); }
}